import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Pagination,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useSearchAdventures } from '../hooks/useAdventures'
import { useSavedFilters } from '../hooks/useSavedFilters'
import { AdventureCard } from '../components/AdventureCard'
import { AdventureMap } from '@components/map/AdventureMap'
import { AdventureFilterDrawer } from '../components/AdventureFilterDrawer'
import { ViewModeToggle } from '@components/map/ViewModeToggle'
import { AdventureSearchRequest, AdventureDto } from '@api/types/adventure'
import { useSidebarWidth } from '@components/layout/MainLayout'
import { useGeolocation } from '@hooks/useGeolocation'

export default function AdventureListPage() {
  const [searchRequest, setSearchRequest] = useState<AdventureSearchRequest>({
    page: 1,
    pageSize: 12,
    sortBy: 'createdDate',
    sortDirection: 'desc',
    radiusMiles: 50, // Default 50 mile radius
  })
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('map')
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [mapKey, setMapKey] = useState(() => `map-${Date.now()}`)
  const [selectedFilterId, setSelectedFilterId] = useState<string>('')
  const [viewportBounds, setViewportBounds] = useState<{
    northEast: { lat: number; lng: number }
    southWest: { lat: number; lng: number }
  } | null>(null)
  const { getDefaultFilter, filters } = useSavedFilters()
  const { location } = useGeolocation()
  const { sidebarWidth } = useSidebarWidth()

  // Load default filter on mount
  useEffect(() => {
    const defaultFilter = getDefaultFilter()
    if (defaultFilter) {
      setSearchRequest({
        ...defaultFilter.filterCriteria,
        page: defaultFilter.filterCriteria.page || 1,
        pageSize: defaultFilter.filterCriteria.pageSize || 12,
      })
      setSelectedFilterId(defaultFilter.id)
    }
  }, [filters])

  // Handle viewport changes from map (debounced by MapContainer)
  const handleViewportChange = (bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } }) => {
    console.log('Viewport changed, updating search bounds:', bounds)

    // Validate bounds to prevent issues when zoomed out too far
    const { northEast, southWest } = bounds

    // Check if bounds are valid
    const latValid = northEast.lat > southWest.lat
    const lngValid = Math.abs(northEast.lng - southWest.lng) < 180 // Not wrapping around world

    // Calculate viewport span
    const latSpan = Math.abs(northEast.lat - southWest.lat)
    const lngSpan = Math.abs(northEast.lng - southWest.lng)

    // Prevent searches when viewport is too large (e.g., zoomed out to see whole world)
    // Max reasonable viewport: ~100 degrees lat/lng (about 6900 miles)
    const MAX_VIEWPORT_DEGREES = 100

    if (!latValid || !lngValid || latSpan > MAX_VIEWPORT_DEGREES || lngSpan > MAX_VIEWPORT_DEGREES) {
      console.warn('⚠️ Viewport bounds invalid or too large, using radius search instead', {
        latValid,
        lngValid,
        latSpan,
        lngSpan,
      })
      // Clear viewport bounds to fall back to radius-based search
      setViewportBounds(null)
      return
    }

    setViewportBounds(bounds)
  }

  const handleFilterSelect = (filterId: string) => {
    if (!filterId) {
      // Reset to default
      setSearchRequest({
        page: 1,
        pageSize: 12,
        sortBy: 'createdDate',
        sortDirection: 'desc',
        radiusMiles: 50,
        userLatitude: location?.latitude,
        userLongitude: location?.longitude,
      })
      setSelectedFilterId('')
      return
    }

    const filter = filters.find((f) => f.id === filterId)
    if (filter) {
      setSearchRequest({
        ...filter.filterCriteria,
        page: filter.filterCriteria.page || 1,
        pageSize: filter.filterCriteria.pageSize || 12,
        userLatitude: location?.latitude,
        userLongitude: location?.longitude,
      })
      setSelectedFilterId(filterId)
    }
  }

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Force a new map instance on next mount
      setMapKey(`map-${Date.now()}`)
    }
  }, [])

  // Prepare the final search request with location data
  const finalSearchRequest: AdventureSearchRequest | undefined = (() => {
    if (!location) {
      console.log('No location available yet')
      return undefined // Wait for location
    }

    // Grid view: always use location + radius
    if (viewMode === 'grid') {
      console.log('Grid view: using location + radius')
      return {
        ...searchRequest,
        userLatitude: location.latitude,
        userLongitude: location.longitude,
        radiusMiles: searchRequest.radiusMiles || 50,
      }
    }

    // Map view: use viewport bounds if available, otherwise location + radius
    if (viewMode === 'map') {
      if (viewportBounds) {
        console.log('Map view: using viewport bounds', viewportBounds)
        // Exclude radiusMiles when using viewport bounds
        const { radiusMiles, ...requestWithoutRadius } = searchRequest
        return {
          ...requestWithoutRadius,
          useViewportBounds: true,
          viewportNorthEastLat: viewportBounds.northEast.lat,
          viewportNorthEastLng: viewportBounds.northEast.lng,
          viewportSouthWestLat: viewportBounds.southWest.lat,
          viewportSouthWestLng: viewportBounds.southWest.lng,
          userLatitude: location.latitude,
          userLongitude: location.longitude,
        }
      } else {
        console.log('Map view: no viewport bounds yet, using location + radius')
        return {
          ...searchRequest,
          userLatitude: location.latitude,
          userLongitude: location.longitude,
          radiusMiles: searchRequest.radiusMiles || 50,
        }
      }
    }

    return {
      ...searchRequest,
      userLatitude: location.latitude,
      userLongitude: location.longitude,
      radiusMiles: searchRequest.radiusMiles || 50,
    }
  })()

  const { data, isLoading, error } = useSearchAdventures(finalSearchRequest)

  // Keep previous adventures during loading to prevent marker flashing
  const [adventures, setAdventures] = useState<AdventureDto[]>([])

  useEffect(() => {
    // Only update adventures when we have new data (not during loading)
    if (data?.items) {
      setAdventures(data.items)
    }
  }, [data?.items])

  // Determine if we're waiting for location data
  const isWaitingForLocation = !finalSearchRequest && !error

  const handlePageChange = (_: unknown, page: number) => {
    setSearchRequest({ ...searchRequest, page })
  }

  const handleFilterClick = () => {
    setFilterDrawerOpen(true)
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load adventures. Please try again later.
      </Alert>
    )
  }

  return (
    <>
      {/* View Mode Toggle - Always visible */}
      <Box
        sx={{
          position: viewMode === 'map' ? 'fixed' : 'absolute',
          top: viewMode === 'map' ? { xs: 61, sm: 69 } : 5,
          right: 5,
          zIndex: 1001,
        }}
      >
        <ViewModeToggle
          viewMode={viewMode}
          onChange={setViewMode}
          onFilterClick={handleFilterClick}
        />
      </Box>

      <Box
        sx={{
          position: viewMode === 'map' ? 'fixed' : 'relative',
          top: viewMode === 'map' ? { xs: 56, sm: 64 } : undefined,
          left: viewMode === 'map' ? { xs: 0, md: `${sidebarWidth}px` } : undefined,
          right: 0,
          bottom: 0,
          margin: viewMode === 'map' ? 0 : undefined,
          padding: viewMode === 'map' ? 0 : 3,
          // Use dvh (dynamic viewport height) on mobile to account for browser UI
          height: viewMode === 'map' ? { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' } : undefined,
          width: viewMode === 'map' ? { xs: '100%', md: `calc(100% - ${sidebarWidth}px)` } : undefined,
          transition: viewMode === 'map' ? 'left 0.2s ease-in-out, width 0.2s ease-in-out' : undefined,
        }}
      >
      {/* Grid View */}
      {viewMode === 'grid' && (
        <>
          {/* Loading State for Grid */}
          {(isLoading || isWaitingForLocation) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Grid Content */}
          {!isLoading && !isWaitingForLocation && data && (
            <>
              {adventures.length === 0 ? (
                <Alert severity="info">
                  No adventures found. Try adjusting your filters.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={3}>
                    {adventures.map((adventure: any) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={adventure.id}>
                        <AdventureCard adventure={adventure} />
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination */}
                  {data.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Pagination
                        count={data.totalPages}
                        page={searchRequest.page || 1}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Map View - Always mounted to prevent position reset */}
      {viewMode === 'map' && (
        <Box sx={{ width: '100%', height: '100%' }} key={`${mapKey}-container`}>
          <AdventureMap
            key={mapKey}
            adventures={adventures}
            height="100%"
            enableAutoFollow={true}
            onViewportChange={handleViewportChange}
            showFriendLocations={true}
          />
        </Box>
      )}

      {/* No data loaded yet */}
      {!isLoading && !isWaitingForLocation && !data && (
        <Alert severity="warning">
          No data received from server. Please check the console for details.
        </Alert>
      )}

        {/* Filter Drawer */}
        <AdventureFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          searchRequest={searchRequest}
          onSearchRequestChange={setSearchRequest}
          selectedFilterId={selectedFilterId}
          onSelectedFilterChange={setSelectedFilterId}
        />
      </Box>
    </>
  )
}
